import {
  fetchWithErrorHandling,
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {useMutation} from '@github-ui/react-query'
import type {ReactionContent, ReactionViewerGroup} from '@github-ui/reaction-viewer/ReactionGroupsUtils'
import {usePullRequestBasePath} from '../stores/selectors'
import {setCommentQueryData} from '../page-data/loaders/use-conversation-comments'
import {produce, type Draft} from 'immer'
import {getCurrentUserLogin} from '@github-ui/client-env'

export interface ReactToIssueCommentVariables {
  commentId: string
  reaction: ReactionContent
  viewerHasReacted: boolean
}

export interface ReactToIssueCommentResponse {
  reactionGroups: ReactionViewerGroup[]
}

export function useReactToIssueCommentMutation() {
  const basePath = usePullRequestBasePath()

  return useMutation({
    mutationFn: async ({commentId, reaction, viewerHasReacted}: ReactToIssueCommentVariables) => {
      const apiUrl = `${basePath}/page_data/${
        viewerHasReacted ? PageData.removeCommentReaction : PageData.addCommentReaction
      }`

      const response = await fetchWithErrorHandling(apiUrl, {
        method: 'POST',
        body: {
          reaction,
          issueCommentId: commentId,
        },
      })

      const json = await parseJSONWithBetterErrors(response)
      throwErrorsIfBadResponse(response, json)
      return json as ReactToIssueCommentResponse
    },
    onSuccess: (data, variables, _onMutateResult, context) => {
      setCommentQueryData(context.client, basePath, parseInt(variables.commentId, 10), comment => {
        comment.reactionGroups = data.reactionGroups
      })
    },
  })
}

/** Helper function for displaying optimistic updates. Returns optimistic reaction groups with args applied. */
export function applyVariablesToReactions(
  reactionGroups: ReactionViewerGroup[],
  variables: ReactToIssueCommentVariables,
) {
  function applyToGroup(group: Draft<ReactionViewerGroup>) {
    const currentUserLogin = getCurrentUserLogin()
    if (!currentUserLogin) return

    if (variables.viewerHasReacted) {
      group.totalCount--
      group.reactors = group.reactors.filter(reactor => reactor.login !== currentUserLogin)
      group.reaction.viewerHasReacted = false
    } else {
      group.totalCount++
      group.reactors.push({login: currentUserLogin, typeName: 'User'})
      group.reaction.viewerHasReacted = true
    }
  }

  return produce(reactionGroups, draftGroups => {
    for (const group of draftGroups)
      if (group.reaction.content === variables.reaction) {
        applyToGroup(group)
        return
      }

    // we haven't found a matching group, so create one
    const unreactedGroup: ReactionViewerGroup = {
      reaction: {
        content: variables.reaction,
        viewerHasReacted: false,
      },
      reactors: [],
      totalCount: 0,
    }
    applyToGroup(unreactedGroup)
    draftGroups.push(unreactedGroup)
  })
}
